import React, { useMemo } from "react";
import { Card, Row, Col } from "react-bootstrap";
import ReactEcharts from "echarts-for-react";
import { useTranslation } from "react-i18next";
import { GREEN, YELLOW, RED, GREY, BLUE, shadeColor } from "@helper/colors";

const style = {
  height: "420px",
  width: "100%",
};

// Generate an array of colors from the helper palette to cycle through for dynamic models
const palette = [
  GREEN,
  BLUE,
  YELLOW,
  RED,
  shadeColor(BLUE, 20),
  GREY,
  shadeColor(GREEN, -20),
  shadeColor(RED, -20),
];

export default function CurrentStockChart({ data, colorMap }) {
  const { t } = useTranslation();

  const stockCounts = useMemo(() => {
    const counts = {};
    if (Array.isArray(data)) {
      data.forEach((sensor) => {
        const model = sensor.sensor_type_key || "Unknown";
        if (counts[model] !== undefined) {
          counts[model]++;
        } else {
          counts[model] = 1;
        }
      });
    }
    return counts;
  }, [data]);

  const totalStock = Array.isArray(data) ? data.length : 0;

  const chartData = useMemo(() => {
    return Object.keys(stockCounts)
      .map((model, index) => ({
        name: model,
        value: stockCounts[model],
        itemStyle:
          colorMap && colorMap[model]
            ? { color: colorMap[model] }
            : { color: palette[index % palette.length] },
      }))
      .filter((item) => item.value > 0);
  }, [stockCounts, colorMap]);

  const option = {
    tooltip: { trigger: "item" },
    legend: { show: false },
    title: {
      text: totalStock.toString(),
      subtext: t("dashboard.qc_passed_unassigned"),
      left: "center",
      top: "center",
      textStyle: { fontSize: 24, fontWeight: "bold" },
      subtextStyle: { fontSize: 14 },
    },
    series: [
      {
        name: t("dashboard.qc_passed_unassigned"),
        type: "pie",
        radius: ["50%", "70%"],
        center: ["50%", "50%"],
        avoidLabelOverlap: false,
        label: { show: false, position: "center" },
        labelLine: { show: false },
        cursor: "default",
        data: chartData,
      },
    ],
  };

  return (
    <Card role="dashboard" className="dashboard-panel mt-0">
      <Card.Header className="dashboard-panel-header">
        <h6 className="mb-0">{t("dashboard.qc_passed_unassigned")}</h6>
      </Card.Header>
      <Card.Body>
        <ReactEcharts option={option} style={style} notMerge />
        <div className="dashboard-health-overview">
          <Row>
            {chartData.map((entry, index) => {
              const percentage = totalStock > 0 ? Math.round((entry.value / totalStock) * 100) : 0;
              return (
                <Col lg="4" md={6} xs={6} key={index} className="dashboard-health-item">
                  <span className="dashboard-health-label">{entry.name}</span>
                  <div className="dashboard-health-stats">
                    <span
                      className="dashboard-health-dot"
                      style={{ background: entry.itemStyle.color }}
                    />
                    <span className="dashboard-health-value">{entry.value}</span>
                    <span className="dashboard-health-percent">{percentage}%</span>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      </Card.Body>
    </Card>
  );
}
